import { useEffect, useState } from 'react';
import { 
  Phone, Mail, MapPin, 
  Clock, Map, ClipboardList, CheckCircle2, Send, 
  Info, ShieldCheck, MessageSquare, RefreshCcw
} from 'lucide-react';
import { createMessage } from '../lib/messages';
import { fetchSettings } from '../lib/settings';

export default function Contact() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mapEmbed, setMapEmbed] = useState('');
  const [socials, setSocials] = useState({ facebook: '', instagram: '', linkedin: '' });
  const [settings, setSettings] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Merci de remplir au minimum votre nom, email et message.');
      return;
    }
    if (!consent) {
      setError('Merci de cocher la case de consentement.');
      return;
    }

    setSending(true);
    try {
      await createMessage({
        name,
        email,
        phone,
        subject,
        message,
        consent,
        source: 'contact_page',
      });
      setSuccess('Votre message a bien été envoyé. Nous vous répondrons dès que possible.');
      setName(''); setEmail(''); setPhone(''); setSubject(''); setMessage(''); setConsent(false);
    } catch (e) {
      setError(e.message || "Une erreur est survenue lors de l'envoi.");
    } finally {
      setSending(false);
    }
  }

  useEffect(() => {
    async function loadSettings() {
      try {
        const settings = await fetchSettings();
        if (settings) {
          setSettings(settings);
          setMapEmbed(settings.map_embed || '');
          const s = settings.socials || {};
          setSocials({
            facebook: s.facebook || '',
            instagram: s.instagram || '',
            linkedin: s.linkedin || '',
          });
        }
      } catch (e) { /* silent */ }
    }
    loadSettings();
  }, []);

  const workflowSteps = [
    { title: "Prise de contact", desc: "Présentez votre besoin par téléphone ou via le formulaire ci-contre.", icon: <Phone size={20}/> },
    { title: "Visite sur site", desc: "Déplacement à domicile pour les mesures et conseils techniques.", icon: <MapPin size={20}/> },
    { title: "Devis détaillé", desc: "Proposition claire et chiffrée, adaptée à votre budget et contraintes.", icon: <ClipboardList size={20}/> },
    { title: "Mise en service", desc: "Installation, réglages et explications pour une utilisation sereine.", icon: <ShieldCheck size={20}/> }
  ];

  return (
    <div className="contact-page-signature">
      {/* HEADER SECTION */}
      <section className="contact-header-premium">
        <div className="container-premium">
           <div className="tag-line-premium">AEF 32 • EXPERTISE & PROXIMITÉ EN GASCOGNE</div>
           <h1>Besoin d'un expert pour votre projet ?</h1>
           <p className="contact-subtitle">
             Parlez-nous de votre projet de portail, d'automatisme ou de fermeture. 
             Nous vous accompagnons de la conception à la pose.
           </p>
        </div>
      </section>

      <div className="container-premium contact-grid-wrapper">
        
        {/* LEFT COLUMN: INFO & WORKFLOW */}
        <div className="contact-left-col">
          
          <div className="info-card-premium identity-card">
            <h2 className="title-with-bar">Coordonnées</h2>
            <div className="contact-links-list">
              <a href={`tel:${settings?.phone?.replace(/\s+/g, '') || '0688991963'}`} className="contact-link-item">
                <div className="icon-circle"><Phone size={18}/></div>
                <div className="link-content">
                  <span className="label">Appelez-nous</span>
                  <span className="value">{settings?.phone || '06 88 99 19 63'}</span>
                </div>
              </a>
              <a href={`mailto:${settings?.email || 'contact@aef32.fr'}`} className="contact-link-item">
                <div className="icon-circle"><Mail size={18}/></div>
                <div className="link-content">
                  <span className="label">Écrivez-nous</span>
                  <span className="value">{settings?.email || 'contact@aef32.fr'}</span>
                </div>
              </a>
              <div className="contact-link-item no-link">
                <div className="icon-circle"><MapPin size={18}/></div>
                <div className="link-content">
                  <span className="label">Siège social</span>
                  <span className="value">{settings?.address || '2 chemin des religieuses 32220 Lombez'}</span>
                </div>
              </div>
            </div>

            <div className="social-follow-section">
               <p>Nous suivre sur les réseaux sociaux</p>
               <div className="social-pills-row">
                  {socials.facebook && <a href={socials.facebook} target="_blank" rel="noreferrer" className="social-pill fb"><b>f</b><span>Facebook</span></a>}
                  {socials.instagram && <a href={socials.instagram} target="_blank" rel="noreferrer" className="social-pill ig"><b>IG</b><span>Instagram</span></a>}
                  {socials.linkedin && <a href={socials.linkedin} target="_blank" rel="noreferrer" className="social-pill in"><b>in</b><span>LinkedIn</span></a>}
               </div>
            </div>
          </div>

          <div className="info-card-premium workflow-timeline-card">
            <div className="card-header-with-icon">
               <ClipboardList size={22} className="gold-text"/>
               <h2>Votre projet pas à pas</h2>
            </div>
            <div className="workflow-timeline">
              {workflowSteps.map((step, idx) => (
                <div key={idx} className="timeline-step">
                  <div className="step-number-box">
                    <div className="step-line"></div>
                    <div className="step-icon-circle">{step.icon}</div>
                  </div>
                  <div className="step-text">
                    <h4>{step.title}</h4>
                    <p>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* RIGHT COLUMN: FORM & MAP */}
        <div className="contact-right-col">
          
          <div className="form-card-premium shadow-signature">
            <div className="card-header-with-icon">
               <MessageSquare size={22} className="gold-text"/>
               <h2>Envoyez-nous un message</h2>
            </div>
            <form onSubmit={handleSubmit} className="premium-form-layout">
              {error && <div className="form-alert error-alert"><Info size={18}/> {error}</div>}
              {success && <div className="form-alert success-alert"><CheckCircle2 size={18}/> {success}</div>}
              
              <div className="form-fields-grid">
                <div className="input-field-premium">
                  <label>Votre Nom complet</label>
                  <input value={name} onChange={e => setName(e.target.value)} required placeholder="ex: Jean Dupont" />
                </div>
                <div className="input-field-premium">
                  <label>Adresse E-mail</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="ex: jean@email.com" />
                </div>
                <div className="input-field-premium">
                  <label>Téléphone</label>
                  <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="ex: 06 00 00 00 00" />
                </div>
                <div className="input-field-premium">
                  <label>Sujet de votre demande</label>
                  <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="ex: Rénovation de mon portail" />
                </div>
              </div>

              <div className="input-field-premium full-width">
                <label>Votre Message</label>
                <textarea rows={5} value={message} onChange={e => setMessage(e.target.value)} required placeholder="Décrivez votre projet immobiliers ou vos besoins techniques..." />
              </div>

              <div className="consent-checkbox-premium">
                <label className="checkbox-container">
                  <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} />
                  <span className="checkmark"></span>
                  <p>J'accepte que mes informations soient utilisées uniquement pour me recontacter à propos de ma demande.</p>
                </label>
              </div>

              <button type="submit" className="btn-send-premium" disabled={sending}>
                {sending ? <RefreshCcw size={20} className="spin"/> : <Send size={20} />}
                <span>{sending ? 'Expédition...' : 'Envoyer ma demande'}</span>
              </button>
            </form>
          </div>

          <div className="info-card-premium hours-zone-card">
             <div className="hz-rows">
                <div className="hz-item">
                   <div className="hz-icon"><Clock size={20}/></div>
                   <div className="hz-txt">
                      <h3>Horaires d'ouverture</h3>
                      <p>Du lundi au vendredi : <strong>8h30 – 12h00 / 14h00 – 18h00</strong></p>
                      <p className="hz-note">Interventions possibles le samedi matin sur RDV.</p>
                   </div>
                </div>
                <div className="hz-item">
                   <div className="hz-icon"><Map size={20}/></div>
                   <div className="hz-txt">
                      <h3>Zones d'intervention</h3>
                      <p>Basés en Gascogne, nous intervenons principalement en <strong>Occitanie</strong> (Auch et départements voisins).</p>
                   </div>
                </div>
             </div>
          </div>

        </div>
      </div>

      {mapEmbed && (
        <section className="contact-map-section container-premium">
           <div className="map-frame-signature shadow-signature">
              {(() => {
                const match = (mapEmbed || '').match(/src\s*=\s*"([^"]+)"/i);
                const src = match ? match[1] : (mapEmbed.startsWith('http') ? mapEmbed : '');
                if (src) return <iframe src={src} style={{ border: 0, width: '100%', height: 400 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Localisation AEF32" />;
                return null;
              })()}
           </div>
        </section>
      )}

      <style jsx>{`
        .contact-page-signature { background: #fff; padding-bottom: 80px; min-height: 100vh; font-family: 'Inter', sans-serif; }
        .container-premium { maxWidth: 1200px; margin: 0 auto; padding: 0 20px; }
        
        .contact-header-premium { padding: 40px 0 60px 0; background: #fff; text-align: center; }
        .tag-line-premium { 
           font-size: 11px; font-weight: 800; color: var(--accent); 
           letter-spacing: 2px; margin-bottom: 15px; 
           text-transform: uppercase;
        }
        .contact-header-premium h1 { 
           font-size: clamp(32px, 5vw, 52px); font-weight: 900; 
           color: #0f172a; margin: 0 0 15px 0; letter-spacing: -1.5px;
        }
        .contact-subtitle { font-size: 18px; color: #64748b; max-width: 800px; margin: 0 auto; line-height: 1.6; }

        .contact-grid-wrapper { display: grid; grid-template-columns: 1fr 1.4fr; gap: 40px; margin-top: -20px; position: relative; z-index: 10; }
        
        .info-card-premium { background: #fff; border-radius: 32px; padding: 35px; border: 1px solid #f1f5f9; margin-bottom: 30px; }
        .shadow-signature { box-shadow: 0 20px 50px -12px rgba(15,23,42,0.08); }
        
        .title-with-bar { font-size: 20px; font-weight: 900; color: #0f172a; margin-bottom: 30px; position: relative; }
        .title-with-bar::after { content: ''; position: absolute; bottom: -8px; left: 0; width: 40px; height: 3px; background: var(--accent); border-radius: 99px; }

        .contact-links-list { display: flex; flex-direction: column; gap: 20px; }
        .contact-link-item { display: flex; align-items: center; gap: 18px; text-decoration: none; color: inherit; padding: 10px; border-radius: 16px; transition: all 0.2s; }
        .contact-link-item:hover:not(.no-link) { background: #f8fafc; transform: translateX(5px); }
        .icon-circle { width: 44px; height: 44px; border-radius: 14px; background: #f8fafc; display: flex; align-items: center; justify-content: center; color: var(--accent); }
        .link-content .label { display: block; font-size: 11px; font-weight: 800; text-transform: uppercase; color: #94a3b8; letter-spacing: 0.5px; }
        .link-content .value { font-size: 16px; font-weight: 700; color: #0f172a; }

        .social-follow-section { margin-top: 40px; border-top: 1px solid #f1f5f9; padding-top: 30px; }
        .social-follow-section p { font-size: 13px; font-weight: 800; color: #64748b; margin-bottom: 15px; }
        .social-pills-row { display: flex; flex-wrap: wrap; gap: 10px; }
        .social-pill { display: flex; align-items: center; gap: 8px; padding: 10px 18px; border-radius: 99px; font-size: 13px; font-weight: 800; text-decoration: none; transition: all 0.2s; }
        .social-pill.fb { background: #eef2ff; color: #3b82f6; }
        .social-pill.ig { background: #fdf2f8; color: #db2777; }
        .social-pill.in { background: #eff6ff; color: #2563eb; }
        .social-pill:hover { transform: translateY(-3px); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); }

        .card-header-with-icon { display: flex; align-items: center; gap: 15px; margin-bottom: 30px; }
        .card-header-with-icon h2 { font-size: 20px; font-weight: 900; color: #0f172a; }
        .gold-text { color: var(--accent); }

        .workflow-timeline { display: flex; flex-direction: column; gap: 30px; }
        .timeline-step { display: flex; gap: 20px; }
        .step-number-box { display: flex; flex-direction: column; align-items: center; position: relative; }
        .step-icon-circle { width: 36px; height: 36px; border-radius: 50%; background: #fcfcfd; border: 2px solid var(--accent); color: var(--accent); display: flex; align-items: center; justify-content: center; position: relative; z-index: 2; }
        .step-line { position: absolute; top: 18px; bottom: -48px; width: 2px; background: #f1f5f9; z-index: 1; }
        .timeline-step:last-child .step-line { display: none; }
        .step-text h4 { font-size: 16px; font-weight: 800; color: #0f172a; margin-bottom: 4px; }
        .step-text p { font-size: 13px; color: #64748b; line-height: 1.5; }

        .form-card-premium { background: #fff; border-radius: 36px; padding: 45px; margin-bottom: 30px; border: 1px solid #f1f5f9; }
        .form-fields-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px 25px; }
        .input-field-premium { display: flex; flex-direction: column; gap: 8px; }
        .input-field-premium label { font-size: 13px; font-weight: 800; color: #64748b; margin-left: 5px; }
        .input-field-premium input, .input-field-premium textarea { padding: 14px 20px; border-radius: 18px; border: 1.5px solid #f1f5f9; background: #fcfcfd; font-size: 15px; font-weight: 600; color: #0f172a; transition: all 0.2s; }
        .input-field-premium input:focus, .input-field-premium textarea:focus { outline: none; border-color: var(--accent); background: #fff; box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.08); }
        .full-width { grid-column: span 2; margin-top: 10px; }

        .consent-checkbox-premium { margin: 25px 0; }
        .checkbox-container { display: flex; align-items: flex-start; gap: 15px; cursor: pointer; }
        .checkbox-container p { font-size: 14px; color: #64748b; line-height: 1.4; user-select: none; }
        .checkbox-container input { position: absolute; opacity: 0; cursor: pointer; height: 0; width: 0; }
        .checkmark { width: 22px; height: 22px; background-color: #f1f5f9; border-radius: 8px; flex-shrink: 0; position: relative; transition: all 0.2s; border: 1.5px solid transparent; }
        .checkbox-container:hover input ~ .checkmark { background-color: #e2e8f0; }
        .checkbox-container input:checked ~ .checkmark { background-color: var(--accent); border-color: var(--accent); }
        .checkmark:after { content: ""; position: absolute; display: none; left: 8px; top: 4px; width: 5px; height: 10px; border: solid white; border-width: 0 2.5px 2.5px 0; transform: rotate(45deg); }
        .checkbox-container input:checked ~ .checkmark:after { display: block; }

        .btn-send-premium { background: var(--accent); color: #fff; padding: 16px 35px; border-radius: 20px; font-size: 16px; font-weight: 900; border: none; cursor: pointer; display: flex; align-items: center; gap: 12px; transition: all 0.3s; box-shadow: 0 10px 20px -5px rgba(22, 163, 74, 0.2); }
        .btn-send-premium:hover:not(:disabled) { background: var(--accent-600); transform: translateY(-4px); box-shadow: 0 15px 30px -10px rgba(22, 163, 74, 0.4); }
        .btn-send-premium:disabled { opacity: 0.7; cursor: not-allowed; }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        .form-alert { padding: 15px 20px; border-radius: 18px; font-size: 14px; font-weight: 700; display: flex; align-items: center; gap: 12px; margin-bottom: 25px; }
        .error-alert { background: #fff1f2; color: #e11d48; border: 1px solid #ffe4e6; }
        .success-alert { background: #f0fdf4; color: #16a34a; border: 1px solid #dcfce7; }

        .hours-zone-card { padding: 40px; }
        .hz-rows { display: flex; flex-direction: column; gap: 30px; }
        .hz-item { display: flex; gap: 20px; }
        .hz-icon { width: 50px; height: 50px; border-radius: 16px; background: #f0fdf4; border: 1.5px solid var(--accent); display: flex; align-items: center; justify-content: center; color: var(--accent); flex-shrink: 0; }
        .hz-txt h3 { font-size: 17px; font-weight: 900; color: #0f172a; margin-bottom: 8px; }
        .hz-txt p { font-size: 14px; color: #64748b; line-height: 1.5; margin-bottom: 4px; }
        .hz-note { font-style: italic; color: #94a3b8 !important; }

        .contact-map-section { margin-top: 20px; margin-bottom: 60px; }
        .map-frame-signature { border-radius: 40px; overflow: hidden; border: 1px solid #f1f5f9; background: #fff; padding: 10px; }

        @media (max-width: 991px) {
          .contact-grid-wrapper { grid-template-columns: 1fr; gap: 30px; margin-top: 0; }
        }
        @media (max-width: 600px) {
          .contact-header-premium { padding: 60px 0 40px 0; }
          .form-card-premium { padding: 25px; border-radius: 24px; }
          .form-fields-grid { grid-template-columns: 1fr; }
          .full-width { grid-column: span 1; }
          .btn-send-premium { width: 100%; justify-content: center; }
          .info-card-premium { padding: 25px; }
        }
      `}</style>
    </div>
  );
}
